import React from 'react';
import { LucideIcon } from 'lucide-react';

// --- Card ---
interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  title?: string;
  action?: React.ReactNode;
  accent?: string;
  glass?: boolean;
}

export const Card: React.FC<CardProps> = ({ children, className = '', title, action, accent, glass, ...props }) => (
  <div className={`sdc-card relative overflow-hidden transition-[border-color,background-color,box-shadow,transform] duration-300 ${glass ? 'sdc-glass-card' : 'sdc-panel'} ${className}`} {...props}>
    {accent && <div className="absolute top-0 left-0 right-0 h-[2px]" style={{background: `linear-gradient(90deg, ${accent}, transparent 62%)`}}/>}
    {(title || action) && (
      <div className="sdc-card-header flex items-center justify-between gap-4 px-5 py-4 md:px-6">
        {title && <h3 className="sdc-card-title">{title}</h3>}
        {action && <div>{action}</div>}
      </div>
    )}
    <div className="p-5 md:p-6">{children}</div>
  </div>
);

// --- Button ---
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
}

export const Button: React.FC<ButtonProps> = ({ 
  children, variant = 'primary', size = 'md', icon: Icon, className = '', ...props 
}) => {
  const base = "sdc-button inline-flex items-center justify-center font-bold tracking-wide transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-f1-red/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#090b11]";
  
  const variants: Record<string, string> = {
    primary: "bg-f1-red hover:brightness-110 text-white border border-white/10 shadow-[0_12px_30px_color-mix(in_srgb,var(--f1-red)_18%,transparent)]",
    secondary: "bg-white/[0.05] hover:bg-white/[0.09] text-white/75 hover:text-white border border-white/[0.08] hover:border-white/[0.15]",
    danger: "bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20",
    ghost: "bg-transparent hover:bg-white/[0.04] text-white/50 hover:text-white/80",
    outline: "bg-transparent border border-white/10 hover:border-white/20 text-white/70 hover:text-white"
  };

  const sizes: Record<string, string> = {
    xs: "px-2.5 py-1 text-[10px]",
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2.5 text-sm",
    lg: "px-6 py-3 text-sm"
  };

  return (
    <button className={`${base} ${variants[variant]} ${sizes[size]} ${className}`} {...props}>
      {Icon && <Icon className={`${size === 'xs' ? 'w-3 h-3' : size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} ${children ? 'mr-1.5' : ''}`} />}
      {children}
    </button>
  );
};

// --- Badge ---
export const Badge: React.FC<{ children: React.ReactNode; color?: 'red' | 'green' | 'yellow' | 'blue' | 'gray' | 'purple'; className?: string }> = ({ children, color = 'gray', className = '' }) => {
  const colors: Record<string, string> = {
    red: 'bg-red-500/10 text-red-400 border-red-500/20',
    green: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    yellow: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    gray: 'bg-white/[0.05] text-white/50 border-white/[0.08]',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] leading-none font-bold tracking-wide border ${colors[color]} ${className}`}>
      {children}
    </span>
  );
};

// --- Form Input ---
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const Input: React.FC<InputProps> = ({ label, error, className = '', id, ...props }) => {
  const generatedId = React.useId();
  const inputId = id || generatedId;
  const errorId = `${inputId}-error`;

  return (
    <div className="sdc-field mb-4">
      {label && <label htmlFor={inputId} className="block text-white/50 text-[11px] font-bold mb-2 uppercase tracking-[0.13em]">{label}</label>}
      <input
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={`w-full bg-black/20 border ${error ? 'border-red-500/60' : 'border-white/[0.09]'} rounded-[0.95rem] py-3 px-3.5 text-white/90 text-sm leading-tight focus:outline-none focus:border-f1-red/70 focus:bg-white/[0.045] focus:ring-4 focus:ring-f1-red/5 transition-all placeholder:text-white/20 ${className}`}
        {...props}
      />
      {error && <p id={errorId} className="text-red-400 text-[10px] mt-1.5">{error}</p>}
    </div>
  );
};

// --- Select ---
interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    label?: string;
    options: { value: string; label: string }[];
}

export const Select: React.FC<SelectProps> = ({ label, options, className = '', id, ...props }) => {
  const generatedId = React.useId();
  const selectId = id || generatedId;

  return (
    <div className="sdc-field mb-4">
      {label && <label htmlFor={selectId} className="block text-white/50 text-[11px] font-bold mb-2 uppercase tracking-[0.13em]">{label}</label>}
      <select
        id={selectId}
        className={`w-full bg-black/20 border border-white/[0.09] rounded-[0.95rem] py-3 px-3.5 text-white/90 text-sm leading-tight focus:outline-none focus:border-f1-red/70 focus:ring-4 focus:ring-f1-red/5 transition-all ${className}`}
        {...props}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
};
